/**
 * NotificationBell Component
 * Shows unread notifications count with dropdown list
 */

import { useState, useEffect, useRef } from 'react';
import {
  getNotifications,
  markNotificationAsRead,
  dismissNotification,
  type Notification,
} from '@/services/notifications';

/** Notification types that carry a `project_id` and open that project. */
const PROJECT_NOTIFICATIONS = ['project_shared', 'project_liked', 'project_moderated'];

export function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load notifications
  useEffect(() => {
    loadNotifications();

    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      const data = await getNotifications(20, false);
      setNotifications(data);
      setUnreadCount(data.filter((n) => !n.is_read).length);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      try {
        await markNotificationAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.id === notification.id ? { ...n, is_read: true } : n
          )
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    // Handle notification action based on type
    if (notification.type === 'datasheet_update' && notification.data?.game_system_id) {
      window.location.href = `/collection/${notification.data.game_system_id}`;
    } else if (PROJECT_NOTIFICATIONS.includes(notification.type) && typeof notification.data?.project_id === 'string') {
      // Shared, liked and moderated all open the project (the owner's editor, or the shared view)
      window.location.href = `/projects/${encodeURIComponent(notification.data.project_id)}`;
    } else if (notification.type === 'project_share_request') {
      // Neutral on purpose: the request itself is answered on Projects
      window.location.href = '/projects#share-requests';
    } else if (notification.type === 'venue_claim_decided' || notification.type === 'venue_manager_revoked') {
      // The payload is not fixed by the contract: go to the venue if it names one
      const venueId = notification.data?.venueId ?? notification.data?.venue_id;
      if (typeof venueId === 'string') window.location.href = `/near-me/venues/${encodeURIComponent(venueId)}`;
    } else if (notification.data?.battle_id) {
      window.location.href = `/battles/${notification.data.battle_id}`;
    } else if (notification.data?.friend_id) {
      window.location.href = '/you/friends';
    }
  };

  const handleDismiss = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    try {
      await dismissNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      const dismissed = notifications.find((n) => n.id === notificationId);
      if (dismissed && !dismissed.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={() => {
          // Opening the list refreshes it, so something that just happened is already there
          if (!isOpen) void loadNotifications();
          setIsOpen(!isOpen);
        }}
        className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg hover:bg-bg-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring"
        aria-label="Notifications"
      >
        <svg
          aria-hidden="true"
          className="w-6 h-6 text-text-primary"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-accent-secondary text-text-on-accent text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[min(24rem,calc(100vw-2rem))] bg-bg-secondary border border-border-default rounded-lg shadow-lg z-50 max-h-[32rem] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-border-default">
            <h3 className="text-lg font-semibold text-text-primary">Notifications</h3>
            {unreadCount > 0 && (
              <p className="text-sm text-text-muted">{unreadCount} unread</p>
            )}
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1">
            {isLoading ? (
              <div className="p-4 text-center text-text-muted">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-text-muted">
                <p>No notifications</p>
              </div>
            ) : (
              <ul className="divide-y divide-border-subtle">
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 hover:bg-bg-hover cursor-pointer transition-colors ${
                      !notification.is_read ? 'bg-bg-tertiary' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-accent-primary rounded-full flex-shrink-0" />
                          )}
                          <h4 className="text-sm font-semibold text-text-primary truncate">
                            {notification.title}
                          </h4>
                        </div>
                        <p className="text-sm text-text-secondary line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-text-muted mt-1">
                          {formatTimestamp(notification.created_at)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => handleDismiss(notification.id, e)}
                        className="flex-shrink-0 p-1 text-text-muted hover:text-text-primary transition-colors"
                        aria-label="Dismiss"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
